<script setup>
import { ref, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import ContactAPI from 'dashboard/api/contacts';
import Button from 'dashboard/components-next/button/Button.vue';
import { toIntlLocale } from 'shared/helpers/intlLocale';

// SiteUp: exportações acompanhadas na tela. Antes só restava esperar o
// e-mail; se ele não chegasse (spam, job quebrado) não havia onde ver nem
// baixar de novo. Depois de pedir uma exportação, o painel abre e confere a
// lista por alguns minutos até o arquivo novo aparecer.
const POLL_INTERVAL = 5000;
const MAX_POLLS = 24;
// Folga para diferença de relógio entre o navegador e o servidor.
const CLOCK_SKEW_MS = 60000;

const { t, locale } = useI18n();
const panel = ref(null);
const records = ref([]);
const loading = ref(false);
const error = ref(false);
const waiting = ref(false);
const timedOut = ref(false);
let pollTimer;
let polls = 0;
let revision = 0;
let requestedAt = 0;
let disposed = false;

const formatDate = value =>
  value
    ? new Intl.DateTimeFormat(toIntlLocale(locale.value), {
        dateStyle: 'short',
        timeStyle: 'short',
      }).format(new Date(value))
    : '';

const stopWaiting = () => {
  clearTimeout(pollTimer);
  waiting.value = false;
};

const load = async ({ silent = false } = {}) => {
  if (disposed) return;
  clearTimeout(pollTimer);
  revision += 1;
  const request = revision;
  loading.value = !silent;
  error.value = false;
  try {
    const { data } = await ContactAPI.getExports();
    if (request !== revision) return;
    records.value = data?.payload || [];
  } catch {
    if (request === revision) error.value = true;
  } finally {
    if (request === revision) {
      loading.value = false;
      if (waiting.value) {
        const ready = records.value.some(
          item =>
            new Date(item.created_at).getTime() >= requestedAt - CLOCK_SKEW_MS
        );
        if (ready) {
          stopWaiting();
        } else if (polls >= MAX_POLLS || !panel.value?.open) {
          stopWaiting();
          timedOut.value = polls >= MAX_POLLS;
        } else {
          polls += 1;
          pollTimer = setTimeout(() => load({ silent: true }), POLL_INTERVAL);
        }
      }
    }
  }
};

const onToggle = event => {
  // Abrir pelo reveal já dispara a leitura; o toggle não repete.
  if (event.target.open) {
    if (!loading.value) load();
  } else {
    revision += 1;
    stopWaiting();
  }
};

// Chamado quando a exportação é aceita: abre o painel e espera o arquivo.
const reveal = () => {
  requestedAt = Date.now();
  polls = 0;
  timedOut.value = false;
  waiting.value = true;
  if (panel.value && !panel.value.open) panel.value.open = true;
  load();
};

defineExpose({ reveal, load });
onBeforeUnmount(() => {
  disposed = true;
  revision += 1;
  clearTimeout(pollTimer);
});
</script>

<template>
  <details
    ref="panel"
    data-testid="contact-export-history"
    class="mx-4 my-2 rounded-xl border border-n-weak bg-n-solid-1"
    @toggle="onToggle"
  >
    <summary class="min-h-11 cursor-pointer px-4 py-3 text-sm font-medium">
      {{ t('CONTACTS_EXPORT_HISTORY.TITLE') }}
    </summary>
    <div
      class="flex flex-col gap-3 border-t border-n-weak p-4"
      :aria-busy="loading || waiting"
    >
      <p v-if="waiting" role="status" class="m-0 text-sm text-n-slate-11">
        {{ t('CONTACTS_EXPORT_HISTORY.WAITING') }}
      </p>
      <p v-else-if="timedOut" role="status" class="m-0 text-sm text-n-amber-11">
        {{ t('CONTACTS_EXPORT_HISTORY.TIMEOUT') }}
      </p>
      <p v-if="error" role="alert" class="m-0 text-sm text-n-ruby-11">
        {{ t('CONTACTS_EXPORT_HISTORY.ERROR') }}
      </p>
      <p v-else-if="loading" role="status" class="m-0 text-sm text-n-slate-11">
        {{ t('CONTACTS_EXPORT_HISTORY.LOADING') }}
      </p>
      <template v-else>
        <p v-if="!records.length" class="m-0 text-sm text-n-slate-11">
          {{ t('CONTACTS_EXPORT_HISTORY.EMPTY') }}
        </p>
        <ul v-else class="m-0 flex list-none flex-col gap-3 p-0">
          <li
            v-for="item in records"
            :key="item.id"
            class="flex flex-wrap items-center justify-between gap-2 text-sm"
          >
            <span class="flex min-w-0 flex-col gap-1">
              <span>{{ t('CONTACTS_EXPORT_HISTORY.FILE') }}</span>
              <span class="text-xs text-n-slate-11">
                {{ formatDate(item.created_at) }}
              </span>
              <span v-if="item.requested_by" class="text-xs text-n-slate-11">
                {{
                  t('CONTACTS_EXPORT_HISTORY.REQUESTED_BY', {
                    name: item.requested_by,
                  })
                }}
              </span>
            </span>
            <a
              :href="item.download_url"
              class="inline-flex min-h-11 items-center gap-1.5 rounded-xl px-3 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
              data-testid="contact-export-download"
              download
            >
              <span class="i-ph-download-simple size-4" />
              {{ t('CONTACTS_EXPORT_HISTORY.DOWNLOAD') }}
            </a>
          </li>
        </ul>
      </template>
      <div class="flex flex-wrap items-center gap-2">
        <Button
          :label="t('CONTACTS_EXPORT_HISTORY.REFRESH')"
          variant="faded"
          color="slate"
          class="!min-h-11 !rounded-xl"
          :disabled="loading"
          @click="load()"
        />
      </div>
    </div>
  </details>
</template>
